/**
 * MeetingsPage — Meetings hub with Upcoming / Past Meetings / Agendas tabs
 */

import { useState } from 'react';
import { Video, Printer } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useStartMeeting } from '../hooks/useMeetings';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { UpcomingMeetings } from '../components/UpcomingMeetings';
import { PastMeetings } from '../components/PastMeetings';
import { AgendaList } from '../components/AgendaList';
import { MeetingDetailPanel } from '../components/MeetingDetailPanel';

// =============================================================================
// Tab types
// =============================================================================

type ActiveTab = 'upcoming' | 'past' | 'agendas';

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past Meetings' },
  { value: 'agendas', label: 'Agendas' },
];

// =============================================================================
// MeetingsPage
// =============================================================================

export function MeetingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const activeTab: ActiveTab = (rawTab === 'past' || rawTab === 'agendas') ? rawTab : 'upcoming';

  const setActiveTab = (tab: ActiveTab) => {
    setSearchParams((prev) => { prev.set('tab', tab); return prev; }, { replace: true });
  };

  const navigate = useNavigate();
  const startMeeting = useStartMeeting();

  const [teamId, setTeamId] = useState<string>('all');
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);

  const { data: teams = [] } = useTeams();

  async function handleStartMeeting(id: string) {
    await startMeeting.mutateAsync(id);
    navigate(`/os/meetings/${id}`);
  }

  const resolvedTeamId = teamId === 'all' ? undefined : teamId;

  const handlePrintAgenda = () => {
    window.print();
  };

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Video className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Improve alignment and transparency across your organization.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Team filter */}
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Print Agenda */}
          <Button variant="outline" size="sm" onClick={handlePrintAgenda}>
            <Printer className="h-4 w-4 mr-1.5" />
            Print Meeting Agenda
          </Button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'upcoming' && (
        <UpcomingMeetings
          teamId={resolvedTeamId}
          onStartMeeting={handleStartMeeting}
        />
      )}

      {activeTab === 'past' && (
        <PastMeetings teamId={resolvedTeamId} onSelectMeeting={setSelectedMeetingId} />
      )}

      {activeTab === 'agendas' && (
        <AgendaList teamId={resolvedTeamId} />
      )}

      <MeetingDetailPanel
        meetingId={selectedMeetingId}
        open={!!selectedMeetingId}
        onOpenChange={(open) => {
          if (!open) setSelectedMeetingId(null);
        }}
      />
    </div>
  );
}
